import PropTypes from "prop-types";
import { FaPlay } from "react-icons/fa";
import parse from "html-react-parser";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { HiPlus } from "react-icons/hi";
import { IoCheckmarkSharp } from "react-icons/io5";
import { TbMinus } from "react-icons/tb";
import "./video.scss";
import {
  useAddToMyListMutation,
  useGetMyListQuery,
  useRemoveFromMyListMutation,
} from "../api/myListApiSlice";
import useAuth from "../../hooks/useAuth";
import LoadingSpinner from "../../components/loader/Loader";
import { useNavigate } from "react-router-dom";

const Video = ({ video, videos, isActive, onClick, variant = "grid" }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { data, isLoading } = useGetMyListQuery(undefined, {
    skip: !isLoggedIn,
  });

  const myList = data?.myList || [];
  const [addToMyList] = useAddToMyListMutation();
  const [removeFromMyList] = useRemoveFromMyListMutation();

  const isInList = myList?.some((v) => v._id === video._id);

  const handleToggle = async () => {
    try {
      if (isInList) {
        const res = await removeFromMyList(video?._id).unwrap();
        toast.success(res?.message || "Video removed from list");
      } else {
        const res = await addToMyList(video?._id).unwrap();
        toast.success(res?.message || "Video added to your list!");
      }
    } catch (err) {
      // The error object comes from the backend's error-handling middleware
      if (err.status === 404) {
        toast.error("Video not found or user does not exist.");
      } else if (err.status === "FETCH_ERROR") {
        // Handle network errors
        toast.error("Network Error: Please check your connection.");
      } else {
        // Generic error message from the backend
        toast.error(err.data?.message || "An unexpected error occurred.");
      }
    }
  };

  if (isLoading) return <LoadingSpinner />;

  // ---------------- Sidebar variant ----------------
  if (variant === "sidebar") {
    return (
      <motion.div
        className={`sidebar-video ${isActive ? "active-video" : ""}`}
        whileHover={{ scale: 1.02 }}
        onClick={onClick}
      >
        <img
          src={video?.posterUrl || video?.thumbUrl}
          alt={video?.title}
          className="sidebar-thumb"
        />
        <div className="sidebar-info">
          <h4>{video?.title || video?.videoTitle}</h4>
          <p>
            {typeof video?.description === "string"
              ? parse(video?.description.slice(0, 60) + "...")
              : ""}
          </p>
        </div>
      </motion.div>
    );
  }

  // ---------------- Grid / full card variant ----------------
  return (
    <motion.div
      className={`video-card ${isActive ? "active-card" : ""}`}
      whileHover={{ scale: 1.01 }}
      role="button"
      tabIndex="0"
      onClick={onClick}
    >
      <div className="card-image-container">
        <img
          src={video?.posterUrl || video?.thumbUrl}
          alt={`${video?.title} poster`}
          role="img"
          aria-label={video?.title}
        />
        {video?.type === "paid" ? (
          <p className="private-video-message">Member Video</p>
        ) : (
          <p className="public-video-message">Free Video</p>
        )}
      </div>

      <div className="card-details">
        <h3 className="short-title">{video?.title || video?.videoTitle}</h3>
        <p className="short-description">
          {parse(video?.description || video?.videoDescription)}
        </p>

        <div className="card-action">
          <button
            onClick={() =>
              navigate(
                `/watch/${video?.slug || video?._id}?page=${currentPage}`,
                {
                  state: { source: "videoList", videos, page: currentPage },
                }
              )
            }
          >
            Watch Video
          </button>

          {isLoggedIn && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              className="mylist-btn"
            >
              {isInList ? (
                <>
                  <TbMinus /> My List
                </>
              ) : (
                <>
                  <HiPlus /> My List
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Video;
